/**
 * RoutineMemberVaccination — data coordinator for profile / linked-child
 * previous-vaccination only. Mirrors MemberVaccination's pass-through to
 * VaccinationContainer but does NOT call session APIs or touch MemberVaccination.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { uwinAPI } from '../../../../services/uwin.api';
import { toast } from 'react-toastify';
import '../uwin.css';

import {
  transformVaccineData,
  transformVaccinationData,
} from '../VaccinationModule/vaccinationUtils';
import VaccinationContainer from '../VaccinationModule/VaccinationContainer';
import { decryptOpenSSLAES } from '../uwin-decrypt';
import {
  routineEncryptText,
} from './routineVaccinationApi';

const getEligibilityErrorMessage = (response, fallback) => {
  if (response?._isError) {
    return response.error?.response?.data?.message?.error || fallback;
  }
  const message = response?.message ?? response?.data?.message;
  return message?.error || fallback;
};

const RoutineMemberVaccination = ({
  beneficiaryData,
  beneficiaryType: beneficiaryTypeProp,
  onFormComplete,
  toggleDrawer,
  isStepperMode = false,
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [vaccines, setVaccines] = useState([]);
  const [previousVaccinations, setPreviousVaccinations] = useState([]);
  const [detectedBeneficiaryType, setDetectedBeneficiaryType] = useState(
    beneficiaryTypeProp || null
  );
  const [profileData, setProfileData] = useState(null);
  const [administeredVaccinations, setAdministeredVaccinations] = useState([]);
  const [allUipMaterialsRaw, setAllUipMaterialsRaw] = useState([]);
  const [isPrevFailed, setIsPrevFailed] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsPrevFailed(false);

        if (!beneficiaryData?.beneficiary_id) {
          throw new Error('beneficiary_id is required');
        }

        const encryptedBeneficiaryId = await routineEncryptText(
          beneficiaryData.beneficiary_id
        );

        const [materialsResponse, previousVaccinationsResponse, profileResponse] =
          await Promise.all([
            uwinAPI.allUipMaterials(),
            uwinAPI
              .eligibleMaterialsPrevVaccinations({
                beneficiary_id: encryptedBeneficiaryId,
              })
              .catch((error) => ({ _isError: true, error })),
            uwinAPI.getBeneficiaryProfile({
              beneficiary_id: encryptedBeneficiaryId,
            }),
          ]);

        const prevFailed =
          previousVaccinationsResponse?._isError ||
          previousVaccinationsResponse?.data?.success === false ||
          previousVaccinationsResponse?.success === false;

        setIsPrevFailed(prevFailed);

        if (prevFailed) {
          toast.error(
            getEligibilityErrorMessage(
              previousVaccinationsResponse,
              'Failed to load previous vaccination eligibility. Please try again.'
            )
          );
        }

        const transformedVaccines = transformVaccineData(
          materialsResponse,
          decryptOpenSSLAES
        );
        setVaccines(transformedVaccines);
        setAllUipMaterialsRaw(materialsResponse?.data || []);
        setPreviousVaccinations(
          prevFailed
            ? []
            : transformVaccinationData(
                previousVaccinationsResponse,
                transformedVaccines,
                decryptOpenSSLAES,
                materialsResponse?.data
              )
        );

        const fullProfile = profileResponse?.data;
        let resolvedType = beneficiaryTypeProp || null;

        if (!resolvedType) {
          if (fullProfile?.lmc_date) {
            resolvedType = 'PW';
          } else if (fullProfile?.dob) {
            resolvedType = 'CHILD';
          } else {
            throw new Error(
              'Unable to determine beneficiary type - missing LMC date and DOB in profile'
            );
          }
        }

        setDetectedBeneficiaryType(resolvedType);

        const administeredHistory = fullProfile?.vaccination || [];
        setAdministeredVaccinations(administeredHistory);

        if (resolvedType === 'PW') {
          setProfileData({
            lmc_date: fullProfile?.lmc_date,
            vaccination: administeredHistory,
            edd_date: fullProfile?.edd_date || beneficiaryData?.edd_date,
          });
        } else if (resolvedType === 'CHILD') {
          const { calculateChildAgeInDays } = await import(
            '../VaccinationModule/scenarios/children-current'
          );
          const dob = fullProfile?.dob || beneficiaryData?.dob;
          const childAgeInDays = dob ? calculateChildAgeInDays(dob) : 0;
          setProfileData({
            dob,
            childAgeInDays,
            vaccination: administeredHistory,
          });
        }

        setIsDataLoaded(true);
      } catch (error) {
        console.error('RoutineMemberVaccination fetchData error:', error);
        toast.error('Failed to load vaccination data. Please try again.');
        setIsDataLoaded(true);
      }
    };

    if (beneficiaryData?.beneficiary_id) {
      fetchData();
    }
  }, [beneficiaryData?.beneficiary_id, beneficiaryTypeProp]);

  if (!detectedBeneficiaryType && isDataLoaded) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" sx={{ fontSize: 14 }}>
          Unable to determine beneficiary type. Please close and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
        minWidth: '400px',
      }}
    >
      <Box
        sx={{
          py: 2,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            color: '#111827',
          }}
        >
          Record Previous Vaccination
        </Typography>
        {toggleDrawer && (
          <IconButton onClick={toggleDrawer(false)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {detectedBeneficiaryType && isDataLoaded && (
        <VaccinationContainer
          isCurrentFailed={false}
          isPrevFailed={isPrevFailed}
          vaccines={vaccines}
          currentVaccinations={[]}
          previousVaccinations={previousVaccinations}
          profileData={profileData}
          administeredVaccinations={administeredVaccinations}
          allUipMaterialsRaw={allUipMaterialsRaw}
          villages={[]}
          healthVillages={[]}
          wards={[]}
          healthWards={[]}
          beneficiaryData={beneficiaryData}
          beneficiaryType={detectedBeneficiaryType}
          allowedTabs={['previous']}
          isDataLoaded={isDataLoaded}
          locationFormValues={{}}
          onFormComplete={onFormComplete}
          isStepperMode={isStepperMode}
        />
      )}

      {!isDataLoaded && (
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={32} />
          <Typography sx={{ mt: 1.5, fontSize: 14, color: 'text.secondary' }}>
            Loading vaccination data...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RoutineMemberVaccination;
